import { useCallback, useEffect, useRef } from "react";

import { useCurrentOrganization, useCurrentPlatform } from "@/contexts";

import {
  SearchCloseOutcome,
  SearchOpenTrigger,
  trackSearchClosed,
  trackSearchOpened,
  trackSearchQueryExecuted,
  trackSearchResultSelected,
  trackSearchSecondaryAction,
} from "./GlobalSearch.analytics";
import { GlobalSearchItem } from "./GlobalSearch.types";

const generateSessionId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

type SessionState = {
  id: string;
  startedAt: number;
  lastKeyword: string;
  lastResultCount: number;
  selected: boolean;
};

export type UseGlobalSearchAnalyticsReturn = {
  /** Open a session. No-op if one is already live. */
  open: (trigger: SearchOpenTrigger) => void;
  /** Close the live session (if any). Outcome falls back to `abandoned`. */
  close: (outcome?: SearchCloseOutcome) => void;
  recordQueryExecuted: (props: {
    keyword: string;
    page: number;
    resultCount: number;
    items: GlobalSearchItem[];
    latencyMs: number;
  }) => void;
  recordResultSelected: (props: {
    option: GlobalSearchItem;
    items: GlobalSearchItem[];
    wasRecent: boolean;
  }) => void;
  recordSecondaryAction: (props: {
    option: GlobalSearchItem;
    items: GlobalSearchItem[];
    actionKey: string;
  }) => void;
};

/**
 * Owns one global-search analytics session at a time. A session covers the
 * span from popover-open through close, so per-query and per-selection events
 * can be correlated in GA via `search_session_id`. Close auto-emits the
 * outcome (`selected` if `recordResultSelected` fired during the session,
 * otherwise `abandoned`).
 */
export const useGlobalSearchAnalytics = (): UseGlobalSearchAnalyticsReturn => {
  const platform = useCurrentPlatform();
  const federation = useCurrentOrganization();
  const sessionRef = useRef<SessionState | null>(null);

  const commonProps = useCallback(
    (session: SessionState) => ({
      searchSessionId: session.id,
      platformId: platform?.id,
      federationId: federation?.id,
    }),
    [platform?.id, federation?.id]
  );

  const open = useCallback<UseGlobalSearchAnalyticsReturn["open"]>(
    (trigger) => {
      if (sessionRef.current) return;
      const session: SessionState = {
        id: generateSessionId(),
        startedAt: performance.now(),
        lastKeyword: "",
        lastResultCount: 0,
        selected: false,
      };
      sessionRef.current = session;
      trackSearchOpened({ ...commonProps(session), trigger });
    },
    [commonProps]
  );

  const close = useCallback<UseGlobalSearchAnalyticsReturn["close"]>(
    (outcome) => {
      const session = sessionRef.current;
      if (!session) return;
      sessionRef.current = null;
      trackSearchClosed({
        ...commonProps(session),
        outcome: outcome ?? (session.selected ? "selected" : "abandoned"),
        lastKeyword: session.lastKeyword,
        lastResultCount: session.lastResultCount,
        dwellMs: performance.now() - session.startedAt,
      });
    },
    [commonProps]
  );

  const recordQueryExecuted = useCallback<UseGlobalSearchAnalyticsReturn["recordQueryExecuted"]>(
    ({ keyword, page, resultCount, items, latencyMs }) => {
      // No live session means the surface didn't call `open()` (programmatic
      // remount, etc.). Skip rather than fabricating one — sessions without an
      // explicit open would skew dwell/outcome stats.
      const session = sessionRef.current;
      if (!session) return;
      session.lastKeyword = keyword;
      session.lastResultCount = resultCount;
      trackSearchQueryExecuted({
        ...commonProps(session),
        keyword,
        page,
        resultCount,
        items,
        latencyMs,
      });
    },
    [commonProps]
  );

  const recordResultSelected = useCallback<UseGlobalSearchAnalyticsReturn["recordResultSelected"]>(
    ({ option, items, wasRecent }) => {
      const session = sessionRef.current;
      if (!session) return;
      session.selected = true;
      const position = items.findIndex((item) => item.id === option.id);
      const positionWithinType = items
        .filter((item) => item.type === option.type)
        .findIndex((item) => item.id === option.id);
      trackSearchResultSelected({
        ...commonProps(session),
        keyword: session.lastKeyword,
        option,
        position: position >= 0 ? position : -1,
        positionWithinType: positionWithinType >= 0 ? positionWithinType : -1,
        resultCount: items.length,
        wasRecent,
      });
    },
    [commonProps]
  );

  const recordSecondaryAction = useCallback<
    UseGlobalSearchAnalyticsReturn["recordSecondaryAction"]
  >(
    ({ option, items, actionKey }) => {
      const session = sessionRef.current;
      if (!session) return;
      const position = items.findIndex((item) => item.id === option.id);
      trackSearchSecondaryAction({
        ...commonProps(session),
        option,
        actionKey,
        position: position >= 0 ? position : -1,
      });
    },
    [commonProps]
  );

  // Flush a still-open session on unmount so abandoned popovers don't leak
  // dwell-time forever (e.g. user navigates away with the popover open).
  useEffect(() => {
    return () => {
      if (sessionRef.current) close("abandoned");
    };
  }, [close]);

  return { open, close, recordQueryExecuted, recordResultSelected, recordSecondaryAction };
};
